import React from 'react';
import { Button, Icon, Field, TextInput, SelectInput, ConfirmModal } from '../../../components/ui';
import EntityEditor from '../EntityEditor';
import { postJson, del, withOrig, isDraftDirty, moveGuardAssignment } from '../shared';
import { roleLabel } from '../../../roleLabels';

/*
 * UserEditor — the user/guard editor for the unified Setup pane's "Users" tab. Extracted
 * verbatim (CRUD-wise) from the old UsersPane so the exact behavior is preserved:
 *   save   POST api/gateuserNew           (temp-password / manual / keep; siteIds grant sync)
 *   delete DELETE api/gateuserdestroyNew/:id  (self-delete guard + assignment warning)
 *   reset  POST api/gateuserpassreset      (email a temp password, force change at next login)
 *   guard gate-assignment via the shared create-first moveGuardAssignment helper.
 *
 * Carries the dependent-select fixes: honest ENABLED empty prompt + string-normalized value so a
 * customer/subscriber select never shows a phantom selection; a new user created before the customer
 * list loads adopts the default customer once it arrives (so the site picker populates by itself).
 *
 * Contract: props row (null=new), newCtx {subscriberId,customerId}, authId, userId (self),
 * subscriberId (self, for peer-admin anchoring), webToken, customers, subscribers, sites, gates,
 * assignments, toast, onSaved({username,isNew,userId}), onDeleted(), onDirtyChange(bool),
 * refreshUsers()->fresh users array, refreshAssignments().
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmailValid = (v) => { const s = (v || '').trim(); return s.length === 0 || EMAIL_RE.test(s); };
const identityOf = (row) => (row ? `u:${row.id}` : 'u:new');
// a customer row carries its subscriber nested (GateSubscriber) or flat (GateSubscriberId)
const subscriberOf = (c) => (c ? (c.GateSubscriber ? c.GateSubscriber.id : (c.GateSubscriberId || 0)) : 0);
// Site Admin / Guard / Viewer belong to a customer; an Account Admin to a whole subscriber.
const CUSTOMER_ROLES = ['2', '3', '4'];

export default class UserEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = { draft: null, saving: false, deleting: false, confirm: null, reassigning: false, resetting: false, confirmReset: false, pwMode: 'temp', password: '', emailIt: false };
    this._lastDirty = false;
  }

  // Save/delete result is reported UP (the container renders the StatusModal) so the "Saved —
  // give them the password" / "temp emailed to X" message survives this editor unmounting when a
  // create navigates back to the list/roster.
  report = (r) => { if (this.props.onResult) this.props.onResult(r); };

  componentDidMount() { this.buildDraft(); }

  componentDidUpdate(prevProps) {
    if (identityOf(prevProps.row) !== identityOf(this.props.row)) { this.buildDraft(); return; }

    if (this._awaitingDefaultSites && this.isNew() && prevProps.sites !== this.props.sites && this.state.draft) {
      const d = this.state.draft;
      const siteIds = this.defaultGrantIdsFor(d.customerId, d.typeId);
      if (siteIds.length) {
        const next = { ...d, siteIds };
        this.setState({ draft: this.isDirty() ? next : withOrig(next) });
      }
    }

    // race-heal: a NEW user opened before the customers list loaded has customerId 0; adopt the default
    // (or the context) customer once customers arrive so the dependent site/gate pickers populate.
    // [86bcfugmv] Only for roles that belong to a customer (an Account Admin has none), and only from
    // the context subscriber's customers — a Super's list spans every tenant.
    if (!this.props.row && this.state.draft && !this.state.draft.customerId
        && String(this.state.draft.typeId) !== '1'
        && this.props.customers && this.props.customers.length
        && prevProps.customers !== this.props.customers) {
      const d = this.state.draft;
      const ctxId = this.props.newCtx && this.props.newCtx.customerId;
      const pool = d.subscriberId ? this.props.customers.filter((x) => String(subscriberOf(x)) === String(d.subscriberId)) : this.props.customers;
      const c = (ctxId && this.props.customers.find((x) => String(x.id) === String(ctxId))) || pool[0];
      if (c) {
        const subscriberId = subscriberOf(c);
        const next = { ...d, customerId: c.id, subscriberId };
        if (this.isNew() && ['2', '4'].includes(String(d.typeId))) next.siteIds = this.defaultGrantIdsFor(c.id, d.typeId);
        const pristine = !d.firstName && !d.lastName && !d.username && !d.email && !d.phone;
        this.setState({ draft: pristine ? withOrig(next) : next });
      }
    }
    const dirty = this.isDirty() || this.hasPwPending();
    if (dirty !== this._lastDirty) { this._lastDirty = dirty; if (this.props.onDirtyChange) this.props.onDirtyChange(dirty); }
  }

  componentWillUnmount() { if (this._lastDirty && this.props.onDirtyChange) this.props.onDirtyChange(false); }

  pwModeFor = (isNew, typeId) => (!isNew ? 'keep' : (String(typeId) === '3' ? 'manual' : 'temp'));
  isNew = () => !this.props.row;
  isDirty = () => isDraftDirty(this.state.draft);
  hasPwPending = () => this.state.pwMode === 'manual' && (this.state.password || '').length > 0;

  buildDraft = () => {
    const { row, newCtx = {} } = this.props;
    this._awaitingDefaultSites = false;
    this._lastDirty = false;
    if (row) {
      this.setState({
        pwMode: 'keep', password: '', emailIt: false, saving: false, deleting: false, confirm: null, confirmReset: false, saveResult: null,
        draft: withOrig({
          userId: row.id, typeId: row.GateAcl.id,
          firstName: row.sFirstName, lastName: row.sLastName, username: row.sUsername,
          email: row.sEmail || '', phone: row.sPhone || '',
          subscriberId: row.GateSubscriber ? row.GateSubscriber.id : 0,
          customerId: row.GateCustomer ? row.GateCustomer.id : 0,
          siteIds: (row.GateUserSites || []).map((g) => g.GateSiteId).filter((v) => v != null).sort((a, b) => a - b),
          _row: row,
        }),
      });
    } else {
      const custId = newCtx.customerId || 0;
      const cust = custId ? (this.props.customers || []).find((c) => String(c.id) === String(custId)) : null;
      // preset role/gate come from context: the gate's "New guard" opens this pre-set to Guard + gate.
      const presetType = newCtx.typeId ? String(newCtx.typeId) : '4';
      this.setState({
        pwMode: this.pwModeFor(true, presetType), password: '', emailIt: false, saving: false, deleting: false, confirm: null, confirmReset: false, saveResult: null,
        draft: withOrig({
          userId: 0, typeId: presetType, firstName: '', lastName: '', username: '', email: '', phone: '',
          subscriberId: newCtx.subscriberId || (cust && cust.GateSubscriber ? cust.GateSubscriber.id : 0),
          customerId: custId,
          siteIds: this.defaultGrantIdsFor(custId, presetType), gateId: newCtx.gateId ? String(newCtx.gateId) : '',
        }),
      });
    }
    if (this.props.onDirtyChange) this.props.onDirtyChange(false);
  };

  setDraft = (patch) => this.setState((st) => ({ draft: { ...st.draft, ...patch } }));

  onChangeType = (value) => {
    this.setState((st) => {
      const prev = st.draft.typeId;
      const draft = { ...st.draft, typeId: value };
      // [86bcfugmv] An Account Admin is subscriber-wide, but it KEEPS the subscriber the user belongs
      // to. This used to reset a Super's choice to "none" (and the customer to 0 for everyone), so the
      // flip Account Admin -> Site Admin -> Account Admin saved GateSubscriberId 0 and the account
      // vanished (SS Gate master, 2026-10-08). An existing user gets its STORED subscriber back — a
      // customer picked mid-flip (maybe another tenant's) must not move it. The customer stays in the
      // draft, so flipping back to a customer role restores it; save() sends no customer for an Account Admin.
      if (value === '1') {
        const stored = this.props.row && this.props.row.GateSubscriber ? this.props.row.GateSubscriber.id : 0;
        draft.subscriberId = this.props.authId === 100
          ? (stored || draft.subscriberId || this.subscriberOfCustomer(draft.customerId))
          : this.props.subscriberId;
      }
      // New web roles default to the customer's sites. Existing users keep their draft choices
      // through temporary role flips: restoring stored grants would undo deliberate narrowing or
      // reintroduce sites from a previous customer. save() omits manual grants for non-web roles.
      if (this.isNew()) draft.siteIds = this.defaultGrantIdsFor(draft.customerId, value);
      let pwMode = st.pwMode;
      if (this.isNew()) { if (String(value) === '3') pwMode = 'manual'; else if (String(prev) === '3') pwMode = 'temp'; }
      return { draft, pwMode };
    });
  };
  onChangeCustomer = (value) => {
    const subId = this.subscriberOfCustomer(value);
    this.setState((st) => ({ draft: { ...st.draft, customerId: value, subscriberId: subId, siteIds: this.defaultGrantIdsFor(value, st.draft.typeId), gateId: '' } }));
  };
  originalRole = () => (this.props.row && this.props.row.GateAcl ? String(this.props.row.GateAcl.id) : null);
  // [86bcfugmv] Who manages whom (mirrors config/scope.js assertCanWrite): a Super manages everyone; an
  // Account Admin manages every user of its subscriber incl. other Account Admins (not Dividia's); a Site
  // Admin manages the guards/viewers of its sites (not Account Admins or other Site Admins, not itself).
  // A user this admin can't change opens view-only with the reason, instead of a Save the server refuses.
  isViewOnly = () => {
    const { authId, row } = this.props;
    const rowAcl = row && row.GateAcl ? row.GateAcl.id : null;
    return !!row && authId !== 100 && rowAcl != null && (authId === 1 ? [100] : [100, 1, 2]).includes(rowAcl);
  };
  isSelf = () => !!this.props.row && String(this.props.row.id) === String(this.props.userId);
  whoCanChange = () => ({ 100: 'Only Dividia can change a Dividia user.', 1: 'Only an Account Admin or Dividia can change an Account Admin.',
    2: 'Only an Account Admin or Dividia can change a Site Admin.' }[this.originalRole()] || '');
  // the roles this admin may pick, plus the record's own role so the select always shows the truth
  // (a Site Admin is offered Guard/Viewer only — without this a Site Admin row displayed as "Guard")
  roleOptions = () => {
    const offered = (this.props.authId === 100 || this.props.authId === 1) ? ['1', '2', '3', '4'] : ['3', '4'];
    const own = this.originalRole();
    return own && !offered.includes(own) ? [own, ...offered] : offered;
  };
  subscriberOfCustomer = (customerId) => {
    const cust = customerId ? (this.props.customers || []).find((c) => String(c.id) === String(customerId)) : null;
    return cust ? subscriberOf(cust) : 0;
  };
  toggleSite = (siteId) => {
    this._awaitingDefaultSites = false;
    this.setState((st) => {
      const cur = new Set(st.draft.siteIds || []);
      cur.has(siteId) ? cur.delete(siteId) : cur.add(siteId);
      return { draft: { ...st.draft, siteIds: [...cur].sort((a, b) => a - b) } };
    });
  };
  toggleAllSites = (sites) => {
    this._awaitingDefaultSites = false;
    const ids = (sites || []).map((s) => s.id);
    const allOn = ids.length > 0 && ids.every((id) => (this.state.draft.siteIds || []).includes(id));
    this.setDraft({ siteIds: allOn ? [] : ids.slice().sort((a, b) => a - b) });
  };

  // [86bbxga1j] New web-scoped users (Site Admin/Viewer) default to ALL of their customer's sites
  // checked — the common case; the admin unchecks to narrow. Creation-only, web-roles-only, and only
  // once sites are loaded (else []). Guards never call this (their grant mirrors the gate assignment).
  defaultGrantIdsFor = (customerId, typeId) => {
    this._awaitingDefaultSites = false;
    if (!this.isNew() || !customerId || !['2', '4'].includes(String(typeId))) return [];
    this._awaitingDefaultSites = !(this.props.sites || []).length;
    return (this.props.sites || [])
      .filter((s) => String(s.GateCustomerId) === String(customerId))
      .map((s) => s.id).sort((a, b) => a - b);
  };

  save = async () => {
    const d = this.state.draft;
    const isNew = this.isNew();
    const mode = this.state.pwMode;
    const email = (d.email || '').trim();
    this.setState({ saving: true });
    try {
      const accountAdmin = String(d.typeId) === '1';
      const body = {
        id: d.userId, sFirstName: (d.firstName || '').trim(), sLastName: (d.lastName || '').trim(),
        sUsername: (d.username || '').trim(), sEmail: email || null, sPhone: (d.phone || '').trim() || null,
        // [86bcfugmv] an Account Admin carries no customer; every other role's subscriber is its customer's
        GateAclId: d.typeId,
        GateCustomerId: accountAdmin ? 0 : d.customerId,
        GateSubscriberId: accountAdmin ? d.subscriberId : (this.subscriberOfCustomer(d.customerId) || d.subscriberId),
        siteIds: ['2', '4'].includes(String(d.typeId)) ? (d.siteIds || []) : [],
      };
      if (isNew && mode === 'temp') { body.tempPassword = true; body.sendConfirmation = true; }
      else if (mode === 'manual') { body.password = this.state.password.trim(); body.sendConfirmation = !!this.state.emailIt; }
      const res = await postJson('api/gateuserNew', body, this.props.webToken);
      this.setState({ saving: false });
      if (res && res.error === 'username') { this.report({ status: 'error', title: 'Save failed', message: `Username "${d.username}" is already in use.` }); return; }
      if (res && res.error === 'email') { this.report({ status: 'error', title: 'Save failed', message: `Email "${email}" is already in use.` }); return; }
      if (res && res.error === 'sites_required') { this.report({ status: 'error', title: 'Site access required', message: 'This role needs at least one site — select one under "Site access", then save.' }); return; }
      if (res && res.error === 'subscriber_required') { this.report({ status: 'error', title: 'Subscriber required', message: 'An Account Admin must belong to a subscriber — choose one under "Subscriber", then save.' }); return; }
      if (res && res.error === 'customer_required') { this.report({ status: 'error', title: 'Customer required', message: 'This role must belong to a customer — choose one under "Customer", then save.' }); return; }
      if (res && res.error === 'self_role') { this.report({ status: 'error', title: 'Save failed', message: 'You can’t change your own access group — ask another admin.' }); return; }
      if (res && (res.error === 'forbidden_site' || res.error === 'forbidden_scope' || res.error === 'forbidden_acl')) { this.report({ status: 'error', title: 'Not allowed', message: 'You don’t manage this — it belongs to a site or customer outside your access.' }); return; }
      if (res && res.error) { this.report({ status: 'error', title: 'Save failed', message: 'Please review the fields and try again.' }); return; }
      let msg;
      if (isNew && mode === 'temp') msg = `User created — a temporary password was emailed to ${email}.`;
      else if (isNew) msg = this.state.emailIt ? `User created — the password was emailed to ${email}.` : 'User created — give them the password you set.';
      else if (mode === 'manual') msg = this.state.emailIt ? `Password updated — emailed to ${email}.` : 'Password updated.';
      else msg = 'User changes saved.';

      if (isNew) {
        // [86bbxga1j] Use the REAL id the backend now returns. The old resolve-by-username from the
        // refreshed list FAILED for a site-admin: their grant-scoped list omits a just-created grantless
        // guard, so `created` was undefined and the assignment was silently skipped (orphaned guard).
        const users = (await this.props.refreshUsers()) || [];
        let createdId = res && res.id ? res.id : null;
        if (!createdId) { const created = users.find((u) => u.sUsername === body.sUsername && !u.deletedAt); createdId = created ? created.id : null; }
        if (createdId && String(d.typeId) === '3' && d.gateId) {
          const gate = (this.props.gates || []).find((g) => String(g.id) === String(d.gateId));
          try {
            await moveGuardAssignment({ gate, guardId: createdId, existingId: null, webToken: this.props.webToken });
            if (this.props.refreshAssignments) await this.props.refreshAssignments();
          } catch (e) {
            console.error('assign-on-create error', e);
            this.props.toast && this.props.toast.error('User created, but the gate assignment failed — assign it from the gate’s Guards tab.');
          }
        }
        this._lastDirty = false; if (this.props.onDirtyChange) this.props.onDirtyChange(false);
        this.report({ status: 'success', title: 'Saved', message: msg });
        if (this.props.onSaved) this.props.onSaved({ username: body.sUsername, userId: createdId, isNew: true });
      } else {
        this._lastDirty = false; if (this.props.onDirtyChange) this.props.onDirtyChange(false);
        this.setState({ pwMode: 'keep', password: '', emailIt: false, draft: withOrig({ ...d }) });
        this.report({ status: 'success', title: 'Saved', message: msg });
        if (this.props.onSaved) this.props.onSaved({ userId: d.userId, isNew: false });
      }
    } catch (e) {
      console.error('Error:', e);
      this.setState({ saving: false });
      this.report({ status: 'error', title: 'Save failed', message: 'If the problem continues please contact your installer.' });
    }
  };

  askReset = () => this.setState({ confirmReset: true });
  doReset = async () => {
    const d = this.state.draft;
    this.setState({ resetting: true });
    try {
      const res = await postJson('api/gateuserpassreset', { id: d.userId }, this.props.webToken);
      this.setState({ resetting: false, confirmReset: false });
      if (res && res.error === 'noemail') { this.props.toast.error('This user has no email on file — add one first.'); return; }
      if (res && res.error) { this.props.toast.error('Password reset failed. Please try again.'); return; }
      this.props.toast.success(`A temporary password was emailed to ${(d.email || '').trim()}.`);
    } catch (e) {
      console.error('reset error', e);
      this.setState({ resetting: false, confirmReset: false });
      this.props.toast.error('Password reset failed. Please try again.');
    }
  };

  askDelete = () => {
    const row = this.state.draft._row;
    if (row.id.toString() === this.props.userId.toString()) { this.props.toast.error('You may not delete your own user account.'); return; }
    this.setState({ confirm: { row, hasAssignment: row.GateAssignment !== null } });
  };
  doDelete = async () => {
    const row = this.state.confirm.row;
    this.setState({ deleting: true });
    try {
      await del('api/gateuserdestroyNew/' + row.id, this.props.webToken);
      this.setState({ deleting: false, confirm: null });
      this._lastDirty = false; if (this.props.onDirtyChange) this.props.onDirtyChange(false);
      this.report({ status: 'success', title: 'Deleted', message: 'User deleted.' });
      if (this.props.onDeleted) this.props.onDeleted({ row });
    } catch (e) {
      console.log('User delete failed', e);
      this.setState({ deleting: false, confirm: null });
      this.report({ status: 'error', title: 'Delete failed', message: 'If the problem continues please contact your installer.' });
    }
  };

  onCancel = () => {
    if (this.isNew()) { if (this.props.onCancelNew) this.props.onCancelNew(); }
    else if (this.isViewOnly() && this.props.onClose) this.props.onClose();   // view-only "Close" leaves the record
    else this.buildDraft();
  };

  currentAssignment = (userId) => (this.props.assignments || []).find((a) =>
    !a.deletedAt && String(a.GateUserId != null ? a.GateUserId : a.GateUser && a.GateUser.id) === String(userId));
  reassignGuard = async (gateId) => {
    const d = this.state.draft;
    const current = this.currentAssignment(d.userId);
    const gate = (this.props.gates || []).find((g) => String(g.id) === String(gateId));
    this.setState({ reassigning: true });
    try {
      await moveGuardAssignment({ gate, guardId: d.userId, existingId: current ? current.id : null, webToken: this.props.webToken });
      if (this.props.refreshAssignments) await this.props.refreshAssignments();
      this.setState({ reassigning: false });
      this.props.toast.success(gate ? 'Gate assignment updated' : 'Gate assignment removed');
    } catch (e) {
      console.error('reassign error', e);
      this.setState({ reassigning: false });
      this.props.toast.error('Could not update the gate assignment. Please try again.');
    }
  };

  render() {
    const { draft, saving, deleting } = this.state;
    if (!draft) return null;
    const isNew = this.isNew();
    const authId = this.props.authId;
    const isAccountAdmin = String(draft.typeId) === '1';
    const needsCustomer = CUSTOMER_ROLES.includes(String(draft.typeId));
    const showSubscriber = authId === 100 && isAccountAdmin;
    const showCustomer = (authId === 100 || authId === 1) && needsCustomer;
    // [86bcfugmv] Save stays off until the role's tenant is chosen. The "required" marks used to be
    // visual only, so a blank Subscriber saved as 0 — the account then saw nothing and vanished. A
    // subscriber/customer that's no longer live (deleted — e.g. a legacy user on a deleted customer)
    // counts as missing too, once its list has loaded.
    const isLive = (list, id) => !(list || []).length || (list || []).some((x) => String(x.id) === String(id));
    const tenantMissing = isAccountAdmin
      ? (!draft.subscriberId || (showSubscriber && !isLive(this.props.subscribers, draft.subscriberId)))
      : (needsCustomer && (!draft.customerId || !isLive(this.props.customers, draft.customerId)));
    const emailVal = (draft.email || '').trim();
    const emailOk = isEmailValid(emailVal);
    const emailPresent = emailVal.length > 0;
    const { pwMode, password, emailIt } = this.state;
    const isGuard = String(draft.typeId) === '3';
    // [86bcfugmv] a Site Admin's guard works at one of its sites: it must be put on one of its gates (left
    // unassigned it would belong to no site of theirs — and vanish from their own list)
    const needsGate = isNew && isGuard && authId === 2;
    const needEmail = (isNew && pwMode === 'temp') || (pwMode === 'manual' && emailIt);
    const baseValid = !!(draft.firstName && draft.firstName.trim()) && !!(draft.lastName && draft.lastName.trim())
      && !!(draft.username && draft.username.trim()) && emailOk && (!needEmail || emailPresent);
    const trimmedPassword = (password || '').trim();
    const pwValid = pwMode === 'manual' ? trimmedPassword.length >= 8 : true;
    const pwActionPending = !isNew && pwMode === 'manual' && trimmedPassword.length >= 8;
    const gatesForGuard = isGuard ? (this.props.gates || []).filter((g) => g.GateCustomer && String(g.GateCustomer.id) === String(draft.customerId)) : [];
    const isWebScoped = ['2', '4'].includes(String(draft.typeId));   // Site Admin / Viewer: hand-pick site access (guards derive from their gate)
    const sitesForCustomer = isWebScoped ? (this.props.sites || []).filter((s) => String(s.GateCustomerId) === String(draft.customerId)) : [];
    const grantIds = draft.siteIds || [];
    const allSitesSelected = sitesForCustomer.length > 0 && sitesForCustomer.every((s) => grantIds.includes(s.id));
    const canSave = baseValid && pwValid && !tenantMissing && (!needsGate || !!draft.gateId) && (this.isDirty() || pwActionPending);
    const currentAssign = isGuard && !isNew ? this.currentAssignment(draft.userId) : null;
    const currentGateId = currentAssign ? (currentAssign.Gate ? currentAssign.Gate.id : currentAssign.GateId) : '';
    const viewOnly = this.isViewOnly();
    const self = this.isSelf();

    return (
      <>
        <EntityEditor
          chromeless
          title={isNew ? 'New User' : [draft.firstName, draft.lastName].filter(Boolean).join(' ') || 'User'}
          onSubmit={this.save}
          onCancel={this.onCancel}
          onDelete={isNew || viewOnly || self ? undefined : this.askDelete}
          saving={saving}
          deleting={deleting}
          saveLabel={isNew ? 'Create user' : 'Save changes'}
          canSave={canSave && !viewOnly}
          readOnly={viewOnly}
        >
          <fieldset disabled={viewOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <div className="formgrid">
            {viewOnly && (
              <div className="editnote editnote--info">
                <Icon name="shield" size={16} />
                <span><b>View only.</b> {this.whoCanChange()}</span>
              </div>
            )}
            {!isNew && isWebScoped && grantIds.length === 0 && (
              <div className="editnote editnote--warn">
                <Icon name="warning" size={16} />
                <span><b>No web access.</b> This user has no site access, so they’ll see nothing on the dashboard. Grant at least one site under “Site access” below.</span>
              </div>
            )}
            {tenantMissing && !viewOnly && !showSubscriber && !showCustomer && (
              <div className="editnote editnote--warn">
                <Icon name="warning" size={16} />
                <span>{isAccountAdmin
                  ? <><b>No subscriber.</b> This user can’t see any data and can’t be saved until Dividia attaches them to one.</>
                  : <><b>No customer.</b> This user isn’t attached to a customer and can’t be saved until an Account Admin attaches them to one.</>}</span>
              </div>
            )}
            <Field label="Access group" required hint={self && !viewOnly ? 'You can’t change your own access group.' : undefined}>
              <SelectInput value={draft.typeId} disabled={self} onChange={(e) => this.onChangeType(e.target.value)}>
                {this.roleOptions().map((id) => <option key={id} value={id}>{roleLabel(id)}</option>)}
              </SelectInput>
            </Field>

            {showSubscriber && (
              <Field label="Subscriber" required error={tenantMissing ? 'Choose the subscriber this Account Admin manages.' : undefined}>
                <SelectInput value={String(draft.subscriberId || '')} onChange={(e) => this.setDraft({ subscriberId: e.target.value })}>
                  <option value="">Select a subscriber…</option>
                  {this.props.subscribers.map((s) => <option key={s.id} value={s.id}>{s.sName}</option>)}
                </SelectInput>
              </Field>
            )}
            {showCustomer && (
              <Field label="Customer" required error={tenantMissing ? 'Choose the customer this user belongs to.' : undefined}>
                <SelectInput value={String(draft.customerId || '')} onChange={(e) => this.onChangeCustomer(e.target.value)}>
                  <option value="">Select a customer…</option>
                  {this.props.customers.map((c) => <option key={c.id} value={c.id}>{c.sName}</option>)}
                </SelectInput>
              </Field>
            )}

            <Field label="First name" required>
              <TextInput value={draft.firstName} placeholder="enter first name" onChange={(e) => this.setDraft({ firstName: e.target.value })} />
            </Field>
            <Field label="Last name" required>
              <TextInput value={draft.lastName} placeholder="enter last name" onChange={(e) => this.setDraft({ lastName: e.target.value })} />
            </Field>
            <Field label="Username" required hint="Short name used to log in (email works too).">
              <TextInput value={draft.username} placeholder="enter username" onChange={(e) => this.setDraft({ username: e.target.value })} />
            </Field>
            <Field label="Email" required={needEmail}
              error={!emailOk ? 'Enter a valid email address.' : undefined}
              hint={emailOk ? (needEmail ? 'Where the password is emailed — and a login handle.' : 'Optional — enables email login and password-reset emails.') : undefined}>
              <TextInput type="email" value={draft.email} placeholder="name@company.com" onChange={(e) => this.setDraft({ email: e.target.value })} />
            </Field>
            <Field label="Phone" hint="Optional.">
              <TextInput type="tel" value={draft.phone} placeholder="(555) 555-1234" onChange={(e) => this.setDraft({ phone: e.target.value })} />
            </Field>

            {isWebScoped && (
              <Field label="Site access" full required hint="Choose the sites this user can access — or Select all. At least one site is required for this role.">
                {!draft.customerId
                  ? <span className="readonly-val is-empty">Choose a customer first.</span>
                  : sitesForCustomer.length === 0
                    ? <span className="readonly-val is-empty">No sites for this customer yet.</span>
                    : (
                      <div className="sitegrants">
                        <label className={`sitegrant sitegrant--all${allSitesSelected ? ' is-on' : ''}`}>
                          <input type="checkbox" checked={allSitesSelected} onChange={() => this.toggleAllSites(sitesForCustomer)} />
                          <span>Select all sites</span>
                        </label>
                        {sitesForCustomer.map((s) => (
                          <label key={s.id} className={`sitegrant${grantIds.includes(s.id) ? ' is-on' : ''}`}>
                            <input type="checkbox" checked={grantIds.includes(s.id)} onChange={() => this.toggleSite(s.id)} />
                            <span>{s.sName}</span>
                          </label>
                        ))}
                      </div>
                    )}
              </Field>
            )}

            {isGuard && (
              <Field label="Gate assignment" full error={needsGate && !draft.gateId ? 'Choose the gate this guard will work — one of your sites.' : undefined} hint={isNew
                ? 'Pick the gate this guard will man — it’s assigned when you create the user.'
                : 'Which gate this guard mans — change it to reassign them.'}>
                {gatesForGuard.length === 0
                  ? <span className="readonly-val is-empty">{draft.customerId ? "No gates available for this guard's customer." : 'Choose a customer first.'}</span>
                  : isNew
                    ? (
                      <SelectInput value={draft.gateId || ''} onChange={(e) => this.setDraft({ gateId: e.target.value })}>
                        <option value="">{needsGate ? 'Select a gate…' : '— Assign later —'}</option>
                        {gatesForGuard.map((g) => <option key={g.id} value={g.id}>{g.sName}{g.GateSite ? ' · ' + g.GateSite.sName : ''}</option>)}
                      </SelectInput>
                    )
                    : (
                      <SelectInput value={currentGateId} disabled={this.state.reassigning} onChange={(e) => this.reassignGuard(e.target.value)}>
                        <option value="">— Unassigned —</option>
                        {gatesForGuard.map((g) => <option key={g.id} value={g.id}>{g.sName}{g.GateSite ? ' · ' + g.GateSite.sName : ''}</option>)}
                      </SelectInput>
                    )}
              </Field>
            )}

            {isGuard && (
              <div className="editnote editnote--info">
                <Icon name="shield" size={16} />
                <span>This guard signs in on the <b>mobile app</b> and the <b>web dashboard</b>. On both, they can access only their <b>assigned gate’s site</b> — nothing more. Change the gate to change what they see; leave them unassigned and they’ll have no access until you assign one.</span>
              </div>
            )}

            <Field label="Password" full>
              <div className="pwchoice">
                {isNew ? (
                  <>
                    {!isGuard && (
                      <label className={`pwopt${pwMode === 'temp' ? ' is-on' : ''}`}>
                        <input type="radio" name="pwmode" checked={pwMode === 'temp'} onChange={() => this.setState({ pwMode: 'temp' })} />
                        <span className="pwopt__body"><span className="pwopt__title">Email a temporary password</span><span className="pwopt__hint">The user sets their own password the first time they log in. Requires an email address.</span></span>
                      </label>
                    )}
                    <label className={`pwopt${pwMode === 'manual' ? ' is-on' : ''}`}>
                      {!isGuard && <input type="radio" name="pwmode" checked={pwMode === 'manual'} onChange={() => this.setState({ pwMode: 'manual' })} />}
                      <span className="pwopt__body"><span className="pwopt__title">Set a password now</span><span className="pwopt__hint">{isGuard ? 'Guards sign in on the mobile app — set their password here and share it with them. (Temporary passwords aren’t available for guards.)' : 'You choose the password and give it to the user; they won’t be forced to change it.'}</span></span>
                    </label>
                  </>
                ) : (
                  <>
                    <label className={`pwopt${pwMode === 'keep' ? ' is-on' : ''}`}>
                      <input type="radio" name="pwmode" checked={pwMode === 'keep'} onChange={() => this.setState({ pwMode: 'keep', password: '', emailIt: false })} />
                      <span className="pwopt__body"><span className="pwopt__title">Keep current password</span></span>
                    </label>
                    <label className={`pwopt${pwMode === 'manual' ? ' is-on' : ''}`}>
                      <input type="radio" name="pwmode" checked={pwMode === 'manual'} onChange={() => this.setState({ pwMode: 'manual' })} />
                      <span className="pwopt__body"><span className="pwopt__title">Set a new password now</span><span className="pwopt__hint">{isGuard ? 'Replace it with one you choose, then share it with the guard.' : 'Replace the current password with one you choose; the user isn’t forced to change it.'}</span></span>
                    </label>
                  </>
                )}
                {pwMode === 'manual' && (
                  <div className="pwmanual">
                    <TextInput type="password" value={password} placeholder="new password (at least 8 characters)" autoComplete="new-password"
                      invalid={!!password && password.trim().length < 8} onChange={(e) => this.setState({ password: e.target.value })} />
                    {!!password && password.trim().length < 8 && (<div className="ui-field__msg ui-field__msg--error">Use at least 8 characters.</div>)}
                    <label className={`pwemailit${(!emailPresent || !emailOk) ? ' is-disabled' : ''}`}>
                      <input type="checkbox" checked={emailIt && emailPresent && emailOk} disabled={!emailPresent || !emailOk} onChange={(e) => this.setState({ emailIt: e.target.checked })} />
                      <span>Email this password to the user{(!emailPresent || !emailOk) ? ' — add a valid email above' : ''}</span>
                    </label>
                  </div>
                )}
              </div>
            </Field>

            {!isNew && !isGuard && (
              <Field label="Reset password" full
                hint={(!emailPresent || !emailOk) ? 'Add a valid email above to email this user a temporary password.' : 'Emails a temporary password; the user sets a new one at next login. Their current password stops working immediately.'}>
                <Button size="sm" variant="default" icon={<Icon name="mail" size={14} />} disabled={!emailPresent || !emailOk} onClick={this.askReset}>
                  Reset &amp; email a temporary password
                </Button>
              </Field>
            )}
          </div>
          </fieldset>
        </EntityEditor>

        {this.state.confirm && (
          <ConfirmModal open onClose={() => this.setState({ confirm: null })} onConfirm={this.doDelete} onCancel={() => this.setState({ confirm: null })}
            title="Delete this user?" confirmLabel="Delete" danger loading={this.state.deleting}>
            <p className="ui-modal__msg" style={{ marginBottom: 8 }}>This action cannot be undone. <strong>{this.state.confirm.row.sFullName}</strong></p>
            {this.state.confirm.hasAssignment && (
              <div className="devnote" style={{ background: 'var(--ev-denied-bg)', borderColor: 'var(--ev-denied-bd)', color: 'var(--ev-denied)' }}>
                <Icon name="warning" size={16} /><span>There is a gate assignment associated with this user.</span>
              </div>
            )}
          </ConfirmModal>
        )}
        {this.state.confirmReset && (
          <ConfirmModal open onClose={() => this.setState({ confirmReset: false })} onCancel={() => this.setState({ confirmReset: false })} onConfirm={this.doReset}
            title="Reset this user's password?" confirmLabel="Reset &amp; email" cancelLabel="Cancel" loading={this.state.resetting}>
            <p className="ui-modal__msg">A temporary password will be emailed to <strong>{(draft.email || '').trim()}</strong>. They'll be required to set a new password the next time they log in. Their current password stops working immediately.</p>
          </ConfirmModal>
        )}
      </>
    );
  }
}
